<template>
    <div ref="rootRef" class="task-activity-picker" :class="{ 'disabled': disabled }">
        <div class="chip-input form-control" :class="{ 'is-open': isOpen }" @click.self="openDropdown" @mousedown.self.prevent="focusSearch">
            <span
                v-for="chip in modelValue"
                :key="chip.publicId"
                class="chip"
                :title="chip.categoryName ? `${chip.categoryName} / ${chip.name}` : chip.name"
                @mousedown.stop
                @click.stop
            >
                <span class="chip-label">{{ chip.name }}</span>
                <button
                    v-if="!disabled"
                    type="button"
                    class="chip-remove"
                    :aria-label="$t('[[[Usuń]]]')"
                    @click.stop="removeChip(chip)"
                    @mousedown.prevent
                >
                    <i class="fas fa-times"></i>
                </button>
            </span>
            <input
                ref="searchRef"
                v-model="searchText"
                type="text"
                class="chip-search"
                :placeholder="modelValue.length === 0 ? (placeholder || $t('[[[Zacznij pisać aby wyszukać czynność...]]]')) : ''"
                :disabled="disabled"
                @focus="openDropdown"
                @keydown.backspace="onBackspace($event)"
                @keydown.esc="closeDropdown"
                @keydown.enter.prevent
            />
            <i class="fas fa-chevron-down chip-caret" :class="{ 'open': isOpen }"></i>
        </div>

        <div v-if="isOpen" class="picker-dropdown" @mousedown.stop>
            <div class="picker-filters">
                <div class="picker-filter">
                    <select v-model="filterDepartment" class="form-control form-control-sm" @change="onDepartmentChanged">
                        <option v-for="opt in departmentOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                    </select>
                </div>
                <div class="picker-filter">
                    <select v-model="filterCategoryPublicId" class="form-control form-control-sm">
                        <option :value="null">{{ $t('[[[Wszystkie kategorie]]]') }}</option>
                        <option v-for="cat in availableCategories" :key="cat.publicId" :value="cat.publicId">{{ cat.name }}</option>
                    </select>
                </div>
            </div>

            <div class="picker-list">
                <div v-if="loading" class="picker-empty text-center py-3">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>
                <div v-else-if="visibleCategories.length === 0" class="picker-empty text-muted text-center py-3">
                    {{ $t('[[[Brak wyników]]]') }}
                </div>
                <template v-else>
                    <div v-if="favoriteActivities.length > 0" class="picker-category">
                        <div class="picker-category-header">{{ $t('[[[Ulubione]]]') }}</div>
                        <label
                            v-for="fav in favoriteActivities"
                            :key="`fav-${fav.activity.publicId}`"
                            class="picker-activity"
                        >
                            <input
                                type="checkbox"
                                :checked="isSelected(fav.activity.publicId)"
                                @change="toggleActivity(fav.activity, fav.category)"
                            />
                            <span class="picker-activity-name">{{ fav.activity.name }}</span>
                            <button
                                type="button"
                                class="picker-favorite active"
                                :title="$t('[[[Usuń z ulubionych]]]')"
                                @click.prevent.stop="toggleFavorite(fav.activity)"
                            >
                                <i class="fas fa-star"></i>
                            </button>
                        </label>
                    </div>
                    <div v-for="cat in visibleCategories" :key="`${cat.department}-${cat.publicId}`" class="picker-category">
                        <div class="picker-category-header">{{ cat.departmentLabel }} - {{ cat.name }}</div>
                        <label
                            v-for="act in cat.visibleActivities"
                            :key="act.publicId"
                            class="picker-activity"
                        >
                            <input
                                type="checkbox"
                                :checked="isSelected(act.publicId)"
                                @change="toggleActivity(act, cat)"
                            />
                            <span class="picker-activity-name">{{ act.name }}</span>
                            <button
                                type="button"
                                class="picker-favorite"
                                :class="{ 'active': isFavorite(act.publicId) }"
                                :title="isFavorite(act.publicId) ? $t('[[[Usuń z ulubionych]]]') : $t('[[[Dodaj do ulubionych]]]')"
                                @click.prevent.stop="toggleFavorite(act)"
                            >
                                <i :class="isFavorite(act.publicId) ? 'fas fa-star' : 'far fa-star'"></i>
                            </button>
                        </label>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
// Chip-input multiselect for task activities. Replaces the previous single-select autocomplete
// so a task's "name" is expressed as its selected activities (per HIR-137 redesign, Aug 2026).
//
// v-model contract: Array<Chip> where Chip = { publicId, name, categoryPublicId?, categoryName? }.
// Full objects rather than string IDs so the parent (TaskForm) can hydrate chip labels from the
// task's existing assignments without needing to re-fetch names — the AssignmentListItem shape
// already carries the labels we need.
//
// Filter defaults: on open, the department filter is preset to the task's department (from prop),
// but the user can switch to browse activities from other departments — supports the
// "task transferred between departments" flow called out in the design brief.
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from '@/helpers/Decorators';
import { DepartmentOptions } from '@/modules/renter/tasks/services/TasksService';
import TaskActivityCategoriesService, { CategoryActivityItem, CategoryWithActivitiesModel, sortByDepartmentOrder } from '@/modules/renter/taskActivities/services/TaskActivityCategoriesService';

type PickerCategory = CategoryWithActivitiesModel & { departmentLabel: string };

@Options({
    name: 'task-activity-picker',
    emits: ['update:modelValue']
})
export default class TaskActivityPicker extends Vue
{
    @Prop({ default: () => [] }) public modelValue: ActivityChip[];
    @Prop({ default: null }) public department: string;
    @Prop({ default: '' }) public placeholder: string;
    @Prop({ default: false }) public disabled: boolean;

    public isOpen: boolean = false;
    public loading: boolean = false;
    public searchText: string = '';
    public filterDepartment: string = null;
    public filterCategoryPublicId: string = null;
    public allCategories: PickerCategory[] = [];
    public favoriteIds: string[] = [];
    public departmentOptions = DepartmentOptions();

    private categoriesLoaded: boolean = false;
    private favoritesInFlight: Set<string> = new Set();

    private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

    // Initialise filterDepartment before first render so the chips-visible branch doesn't briefly
    // paint against a null filter. mounted() runs after first render — moving this into beforeMount
    // guarantees the DOM never sees the pre-init state.
    public beforeMount(): void
    {
        this.filterDepartment = this.department || (this.departmentOptions[0]?.value as string) || null;
    }

    public mounted(): void
    {
        this.outsideClickHandler = (e: MouseEvent) =>
        {
            const root = this.$refs.rootRef as HTMLElement;

            if (root && !root.contains(e.target as Node))
                this.closeDropdown();
        };
        // Capture phase: some modal wrappers stop propagation of outside mousedown.
        document.addEventListener('mousedown', this.outsideClickHandler, true);
    }

    public beforeUnmount(): void
    {
        if (this.outsideClickHandler)
            document.removeEventListener('mousedown', this.outsideClickHandler, true);
    }

    @Watch('department')
    public onTaskDepartmentChanged(value: string): void
    {
        // Only reset the picker's department filter to match the task when the picker is closed —
        // otherwise we'd yank the user's browsing context out from under them mid-selection.
        if (!this.isOpen && value)
        {
            this.filterDepartment = value;
            this.filterCategoryPublicId = null;
        }
    }

    public get availableCategories(): PickerCategory[]
    {
        return this.allCategories.filter(cat => cat.department === this.filterDepartment);
    }

    // With search text: searches every department and category, ignoring both dropdowns.
    // Without: shows the selected department, narrowed by the category dropdown.
    public get visibleCategories(): Array<PickerCategory & { visibleActivities: CategoryActivityItem[] }>
    {
        const search = this.searchText.trim().toLowerCase();

        const source = search
            ? this.allCategories
            : this.availableCategories.filter(cat => !this.filterCategoryPublicId || cat.publicId === this.filterCategoryPublicId);

        return source
            .map(cat => ({
                ...cat,
                visibleActivities: !search || cat.name.toLowerCase().includes(search)
                    ? cat.activities
                    : cat.activities.filter(act => act.name.toLowerCase().includes(search))
            }))
            .filter(cat => cat.visibleActivities.length > 0);
    }

    // HIR-399: derived from visibleCategories so favourites follow the same department/category/search
    // context — favourites from other departments only appear after switching the filter to them.
    public get favoriteActivities(): Array<{ activity: CategoryActivityItem, category: PickerCategory }>
    {
        return this.visibleCategories.flatMap(cat => cat.visibleActivities
            .filter(act => this.isFavorite(act.publicId))
            .map(act => ({ activity: act, category: cat })));
    }

    public get favoriteIdSet(): Set<string>
    {
        return new Set(this.favoriteIds);
    }

    public isFavorite(publicId: string): boolean
    {
        return this.favoriteIdSet.has(publicId);
    }

    public async toggleFavorite(act: CategoryActivityItem): Promise<void>
    {
        if (this.favoritesInFlight.has(act.publicId)) return;

        const wasFavorite = this.isFavorite(act.publicId);

        this.favoriteIds = wasFavorite
            ? this.favoriteIds.filter(id => id !== act.publicId)
            : [...this.favoriteIds, act.publicId];

        try
        {
            this.favoritesInFlight.add(act.publicId);

            if (wasFavorite)
                await TaskActivityCategoriesService.removeFavorite(act.publicId);
            else
                await TaskActivityCategoriesService.addFavorite(act.publicId);
        }
        catch (ex)
        {
            // Revert only this id — other stars may have been toggled while the request was in flight.
            this.favoriteIds = wasFavorite
                ? [...this.favoriteIds, act.publicId]
                : this.favoriteIds.filter(id => id !== act.publicId);
            this.$log.debug(ex);
        }
        finally
        {
            this.favoritesInFlight.delete(act.publicId);
        }
    }

    public get selectedIds(): Set<string>
    {
        return new Set(this.modelValue.map(c => c.publicId));
    }

    public isSelected(publicId: string): boolean
    {
        return this.selectedIds.has(publicId);
    }

    public openDropdown(): void
    {
        if (this.disabled || this.isOpen) return;

        this.isOpen = true;
        this.loadCategories();
        this.focusSearch();
    }

    public closeDropdown(): void
    {
        this.isOpen = false;
        this.searchText = '';
    }

    public focusSearch(): void
    {
        // Skip when the search input is already the active element — the input's own @focus
        // handler calls openDropdown → focusSearch, so a naïve implementation loops focus events
        // on some browsers (Safari especially).
        this.$nextTick(() =>
        {
            const search = this.$refs.searchRef as HTMLInputElement | undefined;

            if (search && document.activeElement !== search)
                search.focus();
        });
    }

    public onDepartmentChanged(): void
    {
        this.filterCategoryPublicId = null;
    }

    // One request for the whole dictionary per picker instance; department/category filters and
    // search are client-side. The `loading` guard dedupes a close/reopen while the request is in flight.
    private async loadCategories(): Promise<void>
    {
        if (this.categoriesLoaded || this.loading) return;

        try
        {
            this.loading = true;

            // HIR-320: department dropdown and category grouping follow the "Biblioteka czynności" order.
            const [categories, departmentOrder] = await Promise.all([
                TaskActivityCategoriesService.getCategoriesWithActivities(),
                TaskActivityCategoriesService.getDepartmentsOrder()
            ]);

            this.departmentOptions = sortByDepartmentOrder(this.departmentOptions, departmentOrder);

            const order = this.departmentOptions.map(o => o.value as string);

            this.allCategories = categories
                .filter(cat => order.includes(cat.department))
                .sort((a, b) => order.indexOf(a.department) - order.indexOf(b.department))
                .map(cat => ({ ...cat, departmentLabel: this.departmentOptions.find(o => o.value === cat.department)?.text || '' }));
            this.favoriteIds = categories.flatMap(cat => cat.activities.filter(act => act.isFavorite).map(act => act.publicId));
            this.categoriesLoaded = true;
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
        finally
        {
            this.loading = false;
        }
    }

    public toggleActivity(act: CategoryActivityItem, cat: PickerCategory): void
    {
        // Department dropdown is a pure browsing filter — any activity from any department is
        // a valid pick. Backend accepts cross-department assignments; see
        // SyncTaskActivityAssignmentsHelper (cross-department invariant dropped).
        if (this.isSelected(act.publicId))
        {
            this.emitChange(this.modelValue.filter(c => c.publicId !== act.publicId));

            return;
        }

        this.emitChange([...this.modelValue, {
            publicId: act.publicId,
            name: act.name,
            categoryPublicId: cat.publicId,
            categoryName: cat.name
        }]);
    }

    public removeChip(chip: ActivityChip): void
    {
        this.emitChange(this.modelValue.filter(c => c.publicId !== chip.publicId));
    }

    public onBackspace(event: KeyboardEvent): void
    {
        // Only pop when: search box is empty (otherwise let backspace edit the search text) AND
        // the search input is the actual event target (a chip's remove button focused via Tab
        // would otherwise steal the backspace and pop a different chip than the one focused).
        if (event.target !== this.$refs.searchRef) return;

        if (this.searchText === '' && this.modelValue.length > 0)
            this.emitChange(this.modelValue.slice(0, -1));
    }

    private emitChange(next: ActivityChip[]): void
    {
        this.$emit('update:modelValue', next);
    }
}

export interface ActivityChip {
    publicId: string;
    name: string;
    categoryPublicId?: string;
    categoryName?: string;
}
</script>

<style lang="scss" scoped>
.task-activity-picker {
    position: relative;
    width: 100%;

    &.disabled {
        opacity: 0.6;
        pointer-events: none;
    }
}

.chip-input {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    min-height: 38px;
    padding: 4px 28px 4px 8px;
    cursor: text;
    position: relative;

    &.is-open {
        border-color: #ff8a17;
        box-shadow: 0 0 0 0.2rem rgba(255, 138, 23, 0.15);
    }
}

.chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: #e9ecef;
    border-radius: 3px;
    font-size: 0.85rem;
    line-height: 1.4;
    max-width: 100%;
}

.chip-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}

.chip-remove {
    background: transparent;
    border: 0;
    padding: 0 2px;
    color: #6c757d;
    cursor: pointer;
    font-size: 0.75rem;

    &:hover {
        color: #dc3545;
    }
}

.chip-search {
    flex: 1;
    min-width: 80px;
    border: 0;
    outline: 0;
    background: transparent;
    padding: 4px;
    font-size: 0.9rem;
}

.chip-caret {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    transition: transform 0.15s ease;
    pointer-events: none;

    &.open {
        transform: translateY(-50%) rotate(180deg);
    }
}

.picker-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 1050;
    max-height: 400px;
    display: flex;
    flex-direction: column;
}

.picker-filters {
    display: flex;
    gap: 8px;
    padding: 8px;
    border-bottom: 1px solid #e9ecef;
}

.picker-filter {
    flex: 1;
}

.picker-list {
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 4px 0;
    flex: 1;
}

.picker-empty {
    padding: 12px;
}

.picker-category-header {
    padding: 6px 12px 2px;
    font-size: 0.75rem;
    text-transform: none;
    color: #6c757d;
    font-weight: 500;
}

.picker-activity {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    cursor: pointer;
    margin: 0;
    font-weight: normal;

    &:hover {
        background: #f8f9fa;
    }

    input[type="checkbox"] {
        margin: 0;
    }
}

.picker-activity-name {
    font-size: 0.9rem;
}

// Non-favourite stars are revealed on row hover to match the reference design, where only
// favourites show a (filled) star.
.picker-favorite {
    margin-left: auto;
    background: transparent;
    border: 0;
    padding: 0 2px;
    color: #6c757d;
    cursor: pointer;
    font-size: 0.85rem;
    opacity: 0;

    &.active {
        color: #ff8a17;
        opacity: 1;
    }

    &:focus-visible {
        opacity: 1;
    }
}

.picker-activity:hover .picker-favorite {
    opacity: 1;
}
</style>

<!-- Dark-mode overrides are in an unscoped block so they can key off the ancestor .c-dark-theme
     class (added to <body> by ThemeMode.vue). All selectors nest under .task-activity-picker so
     picker-specific class names like .picker-dropdown / .picker-notice can't leak into other
     components that happen to use the same names. The dropdown is NOT teleported — it renders
     as a child of .task-activity-picker in the DOM. Values chosen to match the palette established
     in assets/scss/theme-mode/_dark.scss. -->
<style lang="scss">
.c-dark-theme {
    .task-activity-picker {
        .chip {
            background: hsla(0, 0%, 100%, 0.1);
            color: var(--color);
        }

        .chip-remove {
            color: hsla(0, 0%, 100%, 0.6);

            // No themed danger token exists in _dark.scss — this red is picked to remain legible
            // against the dark chip background (darker than the base #dc3545 which reads muddy).
            &:hover {
                color: #ff6b6b;
            }
        }

        .chip-caret {
            color: hsla(0, 0%, 100%, 0.6);
        }

        .picker-dropdown {
            background: var(--primary-menu-color);
            border-color: hsla(0, 0%, 100%, 0.15);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        }

        .picker-filters {
            border-bottom-color: hsla(0, 0%, 100%, 0.09);
        }

        .picker-category-header {
            color: hsla(0, 0%, 100%, 0.6);
        }

        .picker-activity {
            &:hover {
                background: hsla(0, 0%, 100%, 0.05);
            }
        }

        .picker-favorite:not(.active) {
            color: hsla(0, 0%, 100%, 0.6);
        }
    }
}
</style>
